import { useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

export function formatDateDDMMYY(dateKey) {
  const [y, m, d] = dateKey.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

// Popover/list date-mismatch fix (Feb 2026): formats the backend's
// `effective_date` ("YYYY-MM-DD", same IST convention the grid already
// groups by) instead of ever re-deriving a date from `created_at` in the
// BROWSER'S OWN local timezone, which could drift a day off the grid.
export function formatDateDDMMYYYY(dateKey) {
  if (!dateKey) return "—";
  const [y, m, d] = dateKey.split("-");
  return `${d}/${m}/${y}`;
}

export function BackfillDialog({ asset, dateKey, shift, onClose, onSaved }) {
  const [reading, setReading] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!reading.trim()) return toast.error("Reading is required");
    setBusy(true);
    try {
      await client.post("/fleetcare/daily-log/backfill", {
        asset_id: asset.asset_id,
        business_date: dateKey,
        shift,
        current_reading: reading.trim(),
        remarks: remarks.trim() || undefined,
      });
      toast.success("Entry added and marked Verified");
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md" data-testid="daily-log-backfill-panel">
        <SheetHeader>
          <SheetTitle>Add Missing Entry</SheetTitle>
          <SheetDescription>
            Saves directly as Verified — no photo or AI check for backfilled entries.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">Machine</p>
              <p className="text-sm font-medium" data-testid="backfill-machine">{asset.asset_label}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">Site</p>
              <p className="text-sm font-medium" data-testid="backfill-site">{asset.site_label || "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">Date</p>
              <p className="text-sm font-medium" data-testid="backfill-date">{formatDateDDMMYY(dateKey)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">Shift</p>
              <p className="text-sm font-medium" data-testid="backfill-shift">{shift}</p>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">
              HMR Reading <span className="text-red-500">*</span>
            </p>
            <Input
              data-testid="backfill-reading-input"
              value={reading}
              onChange={(e) => setReading(e.target.value)}
              placeholder="HMR reading"
            />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Remarks</p>
            <Textarea data-testid="backfill-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          <Button
            data-testid="backfill-save-button"
            disabled={busy}
            onClick={save}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {busy ? "Saving..." : "Save Entry"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
